import { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

export function getPersistedStorageKey(key: string, pathname: string) {
  return `persisted-${key}-${pathname}`
}

export function readPersistedState<T>(key: string, pathname: string): T | null {
  try {
    const stored = sessionStorage.getItem(getPersistedStorageKey(key, pathname))
    return stored ? (JSON.parse(stored) as T) : null
  } catch {
    return null
  }
}

export function removePersistedState(key: string, pathname: string) {
  sessionStorage.removeItem(getPersistedStorageKey(key, pathname))
}

export function hasPersistedDraft(
  key: string,
  pathname: string,
  draftField: string = 'formData'
): boolean {
  const stored = readPersistedState<Record<string, unknown>>(key, pathname)
  if (!stored) return false

  const draft = stored[draftField]
  if (draft == null) return false
  if (typeof draft === 'object') return Object.keys(draft as object).length > 0
  return true
}

export function usePersistentFormState<T>(
  key: string,
  initialState: T,
  enabled: boolean = true,
  /** Freeze path for KeepAlive panes so inactive panes don't write to the active route's key. */
  pathnameOverride?: string
) {
  const location = useLocation()
  const pathname = pathnameOverride ?? location.pathname
  const storageKey = getPersistedStorageKey(key, pathname)

  const isRestoredFromStorage = useMemo(() => {
    if (!enabled) return false
    return readPersistedState<T>(key, pathname) !== null
  }, [enabled, key, pathname])

  const [state, setState] = useState<T>(() => {
    if (!enabled) return initialState
    return readPersistedState<T>(key, pathname) ?? initialState
  })

  useEffect(() => {
    if (enabled) {
      sessionStorage.setItem(storageKey, JSON.stringify(state))
    }
  }, [state, storageKey, enabled])

  const setPersistentState = useCallback((newState: T | ((prev: T) => T)) => {
    setState(prev => {
      const value = typeof newState === 'function'
        ? (newState as (prev: T) => T)(prev)
        : newState
      return value
    })
  }, [])

  const clearState = useCallback(() => {
    sessionStorage.removeItem(storageKey)
    setState(initialState)
  }, [storageKey, initialState])

  return [state, setPersistentState, clearState, isRestoredFromStorage] as const
}

export function clearAllPersistentStates() {
  const keysToRemove: string[] = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (key?.startsWith('persisted-')) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach(k => sessionStorage.removeItem(k))
}

export function clearPersistentStateByKey(key: string) {
  const keysToRemove: string[] = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const storageKey = sessionStorage.key(i)
    if (storageKey?.startsWith(`persisted-${key}`)) {
      keysToRemove.push(storageKey)
    }
  }
  keysToRemove.forEach(k => sessionStorage.removeItem(k))
}

export function clearPersistentStatesForPath(pathname: string) {
  const keysToRemove: string[] = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const storageKey = sessionStorage.key(i)
    if (storageKey?.startsWith('persisted-') && storageKey.endsWith(pathname)) {
      keysToRemove.push(storageKey)
    }
  }
  keysToRemove.forEach(k => sessionStorage.removeItem(k))
}

export function hasPersistedStatesForPath(pathname: string): boolean {
  for (let i = 0; i < sessionStorage.length; i++) {
    const storageKey = sessionStorage.key(i)
    if (storageKey?.startsWith('persisted-') && storageKey.endsWith(pathname)) {
      return true
    }
  }
  return false
}
